import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  addKbProjectMember,
  createKbProject,
  getProjectOverview,
  removeKbProjectMember,
  updateKbProject,
  type AddKbProjectMemberInput,
  type CreateKbProjectInput,
  type UpdateKbProjectInput,
} from '../lib/kbProjectsApi';

// CLE-54: direct edit of a curated set of project fields (markdown-first) plus a
// KB-aggregated overview. Distinct from the read-only `/api/projects` hooks.

export function useProjectOverview(codeOrId: string | undefined) {
  return useQuery({
    queryKey: ['project-overview', codeOrId],
    queryFn: () => getProjectOverview(codeOrId as string),
    enabled: Boolean(codeOrId),
  });
}

// CLE-223: manual markdown-first project creation. The backend writes the
// README.md then syncs the DB row, so a successful create invalidates the list.
export function useCreateKbProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateKbProjectInput) => createKbProject(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['projects'] });
      // A task template may have been applied to the new project (CLE-DASH).
      void qc.invalidateQueries({ queryKey: ['tasks'] });
      void qc.invalidateQueries({ queryKey: ['task-estimate-totals'] });
    },
  });
}

// Manual team membership (markdown-first): both mutations rewrite the
// `## Echipa` table and re-derive/clean the DB rows, so refresh the detail
// view, the KB overview, the list and the people/capacity aggregations.
//
// The project identifier here is whatever the caller had at hand — the code
// (`project.code ?? project.id`, from the project tabs) or the id (from the
// /people list). The detail query is keyed by the URL id, so invalidating
// `['project', <code>]` never matched it and the new member only showed up
// after a page reload. Invalidate the whole `['project', …]` /
// `['project-overview', …]` prefixes instead: the caches are small, only the
// mounted query actually refetches, and neither key can go stale again
// depending on which identifier the caller happened to pass.
function invalidateTeam(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: ['project'] });
  void qc.invalidateQueries({ queryKey: ['project-overview'] });
  void qc.invalidateQueries({ queryKey: ['projects'] });
  void qc.invalidateQueries({ queryKey: ['people'] });
  void qc.invalidateQueries({ queryKey: ['capacity'] });
}

export function useAddKbProjectMember(codeOrId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: AddKbProjectMemberInput) => addKbProjectMember(codeOrId, input),
    onSuccess: () => invalidateTeam(qc),
  });
}

export function useRemoveKbProjectMember(codeOrId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (email: string) => removeKbProjectMember(codeOrId, email),
    onSuccess: () => invalidateTeam(qc),
  });
}

// CLE-444: edit a person's weekly hours on a specific project from anywhere
// (e.g. the /people list) without binding the hook to a single project code.
// Upserts the `## Echipa` row markdown-first, so it refreshes the same caches
// as the per-project team mutations.
export function useUpdatePersonProjectAllocation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      projectCodeOrId,
      input,
    }: {
      projectCodeOrId: string;
      input: AddKbProjectMemberInput;
    }) => addKbProjectMember(projectCodeOrId, input),
    onSuccess: () => invalidateTeam(qc),
  });
}

export function useUpdateKbProject(codeOrId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateKbProjectInput) => updateKbProject(codeOrId, input),
    onSuccess: () => {
      // The edit re-derives the DB row; refresh the detail view, the overview
      // aggregation and the list so everything reflects the new values. Keyed
      // by prefix for the same reason as invalidateTeam: `codeOrId` is the
      // code here, while the detail query is keyed by the id.
      void qc.invalidateQueries({ queryKey: ['project'] });
      void qc.invalidateQueries({ queryKey: ['project-overview'] });
      void qc.invalidateQueries({ queryKey: ['projects'] });
    },
  });
}
